"use client";

import { useState, useCallback, useRef, useEffect } from 'react';
import Image from 'next/image';
import { useDropzone } from 'react-dropzone';
import ReactCrop, { type Crop, type PixelCrop, centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Upload, Image as ImageIcon, Loader2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import toast from 'react-hot-toast';

export type ImageCategory =
    | 'CONTENT_CARD'
    | 'ROOM_HEADER'
    | 'LOGO'
    | 'AVATAR'
    | 'GENERAL';

type PickerCategory = ImageCategory | 'all';

export interface ImageAsset {
    id: string;
    url: string;
    thumbnailUrl?: string;
    title?: string;
    description?: string;
    width?: number;
    height?: number;
    category: string;
    source?: string;
    dominantColor?: string;
    createdAt?: string | Date;
    uploadedBy?: {
        id: string;
        firstName: string;
        lastName: string;
    };
}

interface ImagePickerProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSelect: (image: ImageAsset) => void;
    category?: PickerCategory; // Made optional, defaults to CONTENT_CARD
    currentImageId?: string;
    allowUpload?: boolean;
    cropAspect?: number; // e.g., 16/9 for headers, 1 for square logos (grid uses rounded squares, not circles)
    cropCircular?: boolean; // True profile/contact picks: circular mask + cover in grid; use with cropAspect 1
    /** Upload tab: crop UI with no fixed aspect ratio (wide or tall logos). */
    allowFreeCrop?: boolean;
    hideLibraryTab?: boolean; // Hide library tab (e.g., when uploading from Image Library page)
}

export function ImagePicker({
    open,
    onOpenChange,
    onSelect,
    category: initialCategory,
    currentImageId,
    allowUpload = true,
    cropAspect,
    cropCircular = false,
    allowFreeCrop = false,
    hideLibraryTab = false,
}: ImagePickerProps) {
    const [activeTab, setActiveTab] = useState<'company' | 'global' | 'upload'>(hideLibraryTab ? 'upload' : 'company');
    const [images, setImages] = useState<ImageAsset[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<PickerCategory>(initialCategory || 'CONTENT_CARD');

    /** Rounded-full + cover — actual profile/contact avatars (`cropCircular` only). */
    const isCircularGalleryTile = cropCircular;
    /** Dense square tiles for 1:1 crops (avatars or logos); rectangles for widescreen assets. */
    const isSquareGalleryTile = cropCircular || cropAspect === 1;

    const useUploadCropper = !!(cropAspect || cropCircular || allowFreeCrop);

    // Upload state
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [crop, setCrop] = useState<Crop>();
    const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
    const imgRef = useRef<HTMLImageElement>(null);

    // Function to center aspect crop
    function centerAspectCrop(mediaWidth: number, mediaHeight: number, aspect: number) {
        return centerCrop(
            makeAspectCrop(
                {
                    unit: '%',
                    width: 90,
                },
                aspect,
                mediaWidth,
                mediaHeight
            ),
            mediaWidth,
            mediaHeight
        );
    }

    // Handle image load to initialize crop
    const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
        const { width, height } = e.currentTarget;
        if (cropAspect || cropCircular) {
            const aspect = cropAspect || 1;
            setCrop(centerAspectCrop(width, height, aspect));
        } else if (allowFreeCrop) {
            setCrop(
                centerCrop(
                    {
                        unit: '%',
                        width: 90,
                        height: 85,
                    },
                    width,
                    height
                )
            );
        }
    }, [cropAspect, cropCircular, allowFreeCrop]);

    // Calculate image display size to fit container
    const getImageStyle = useCallback(() => {
        return {
            maxWidth: 'min(100%, 500px)',
            maxHeight: '240px',
            width: 'auto',
            height: 'auto',
            objectFit: 'contain' as const,
        };
    }, []);

    // Metadata
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [tags, setTags] = useState('');

    // Fetch images from library
    const fetchImages = useCallback(async (tab: 'company' | 'global' = activeTab as 'company' | 'global') => {
        setIsLoading(true);
        try {
            const params = new URLSearchParams({
                category: selectedCategory,
                limit: '50',
            });

            // For global tab, fetch only global images (tenantId: "0" which maps to null)
            // For company tab, fetch only tenant images (no tenantId param, uses user's tenantId)
            if (tab === 'global') {
                params.set('tenantId', '0');
            }

            const response = await fetch(`/api/images?${params}`);
            if (!response.ok) throw new Error('Failed to fetch images');

            const data = await response.json();
            setImages(data.images || []);
        } catch (error) {
            console.error('Error fetching images:', error);
            toast.error('Failed to load images');
        } finally {
            setIsLoading(false);
        }
    }, [selectedCategory, activeTab]);

    // Load images when dialog opens or tab changes
    useEffect(() => {
        if (open && (activeTab === 'company' || activeTab === 'global')) {
            fetchImages(activeTab);
        }
    }, [open, activeTab, fetchImages]);

    // Handle file drop/select
    const onDrop = useCallback((acceptedFiles: File[]) => {
        const file = acceptedFiles[0];
        if (!file) return;

        setSelectedFile(file);
        setCrop(undefined); // Reset crop when new file is selected
        setCompletedCrop(undefined);
        const reader = new FileReader();
        reader.onload = () => {
            setPreviewUrl(reader.result as string);
        };
        reader.readAsDataURL(file);
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'image/*': ['.png', '.jpg', '.jpeg', '.webp'],
        },
        maxFiles: 1,
        maxSize: 10 * 1024 * 1024, // 10MB
    });

    // Handle crop complete
    const getCroppedImage = useCallback(async (): Promise<Blob | null> => {
        if (!completedCrop || !imgRef.current) return null;

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) return null;

        const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
        const scaleY = imgRef.current.naturalHeight / imgRef.current.height;

        canvas.width = completedCrop.width;
        canvas.height = completedCrop.height;

        ctx.drawImage(
            imgRef.current,
            completedCrop.x * scaleX,
            completedCrop.y * scaleY,
            completedCrop.width * scaleX,
            completedCrop.height * scaleY,
            0,
            0,
            completedCrop.width,
            completedCrop.height
        );

        return new Promise((resolve) => {
            canvas.toBlob(resolve, 'image/jpeg', 0.9);
        });
    }, [completedCrop]);

    // Handle upload
    const handleUpload = async () => {
        if (!selectedFile) return;

        setIsUploading(true);
        try {
            let fileToUpload: File | Blob = selectedFile;

            // If cropped, use cropped image
            if (completedCrop) {
                const croppedBlob = await getCroppedImage();
                if (croppedBlob) {
                    fileToUpload = new File([croppedBlob], selectedFile.name, {
                        type: 'image/jpeg',
                    });
                }
            }

            const formData = new FormData();
            formData.append('file', fileToUpload);
            formData.append('category', selectedCategory === 'all' ? 'CONTENT_CARD' : selectedCategory);
            if (title) formData.append('title', title);
            if (description) formData.append('description', description);
            if (tags) formData.append('tags', tags);

            const response = await fetch('/api/images/upload', {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Upload failed');
            }

            const data = await response.json();
            toast.success('Image uploaded successfully');

            // Select the uploaded image
            onSelect(data.imageAsset);
            onOpenChange(false);

            // Reset form
            setSelectedFile(null);
            setPreviewUrl(null);
            setCrop(undefined);
            setCompletedCrop(undefined);
            setTitle('');
            setDescription('');
            setTags('');
        } catch (error) {
            console.error('Error uploading image:', error);
            toast.error(error instanceof Error ? error.message : 'Failed to upload image');
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-4xl max-h-[90vh]">
                <DialogHeader>
                    <DialogTitle>{hideLibraryTab ? 'Upload Image' : 'Select Image'}</DialogTitle>
                </DialogHeader>

                <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'company' | 'global' | 'upload')}>
                    {!hideLibraryTab && (
                        <TabsList className={cn(
                            "grid w-full",
                            allowUpload ? "grid-cols-3" : "grid-cols-2"
                        )}>
                            <TabsTrigger value="company">Company Library</TabsTrigger>
                            <TabsTrigger value="global">Global Library</TabsTrigger>
                            {allowUpload && <TabsTrigger value="upload">Upload New</TabsTrigger>}
                        </TabsList>
                    )}
                    {hideLibraryTab && allowUpload && null}

                    {!hideLibraryTab && (
                        <>
                            <TabsContent value="company" className="mt-4">
                                <ScrollArea className="h-[375px] pr-4">
                                    {isLoading ? (
                                        <div className={cn(
                                            "grid gap-4",
                                            isSquareGalleryTile ? "grid-cols-4 md:grid-cols-5" : "grid-cols-3"
                                        )}>
                                            {Array.from({ length: isSquareGalleryTile ? 10 : 6 }).map((_, i) => (
                                                <Skeleton
                                                    key={i}
                                                    className={cn(
                                                        isSquareGalleryTile ? "aspect-square" : "aspect-video",
                                                        isCircularGalleryTile ? "rounded-full" : "rounded-lg",
                                                    )}
                                                />
                                            ))}
                                        </div>
                                    ) : images.length === 0 ? (
                                        <div className="flex flex-col items-center justify-center h-64 text-center">
                                            <ImageIcon className="h-12 w-12 text-muted-foreground mb-4" />
                                            <p className="text-muted-foreground">No company images found</p>
                                            {allowUpload && (
                                                <Button
                                                    variant="outline"
                                                    className="mt-4"
                                                    onClick={() => setActiveTab('upload')}
                                                >
                                                    Upload your first image
                                                </Button>
                                            )}
                                        </div>
                                    ) : (
                                        <div className={cn(
                                            "grid gap-4",
                                            isSquareGalleryTile ? "grid-cols-4 md:grid-cols-5" : "grid-cols-3"
                                        )}>
                                            {images.map((image) => (
                                                <button
                                                    key={image.id}
                                                    onClick={() => {
                                                        onSelect(image);
                                                        onOpenChange(false);
                                                    }}
                                                    className={cn(
                                                        'group relative overflow-hidden border-2 transition-all',
                                                        isCircularGalleryTile
                                                            ? 'aspect-square rounded-full'
                                                            : isSquareGalleryTile
                                                              ? 'aspect-square rounded-lg'
                                                              : 'aspect-video rounded-lg',
                                                        currentImageId === image.id
                                                            ? 'border-primary'
                                                            : 'border-transparent hover:border-primary/50'
                                                    )}
                                                >
                                                    <Image
                                                        unoptimized
                                                        src={`/api/content-library/proxy-image?url=${encodeURIComponent(isCircularGalleryTile ? image.url : (image.thumbnailUrl || image.url))}`}
                                                        alt={image.title || 'Image'}
                                                        fill
                                                        className={cn(
                                                            "transition-transform group-hover:scale-105",
                                                            isCircularGalleryTile ? "object-cover" : "object-contain bg-muted/50"
                                                        )}
                                                        sizes={isSquareGalleryTile ? "(max-width: 768px) 25vw, 20vw" : "(max-width: 768px) 100vw, 33vw"}
                                                    />
                                                    {!isCircularGalleryTile && image.title && (
                                                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                                                            <p className="text-white text-sm truncate">{image.title}</p>
                                                        </div>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </ScrollArea>
                            </TabsContent>

                            <TabsContent value="global" className="mt-4">
                                <ScrollArea className="h-[375px] pr-4">
                                    {isLoading ? (
                                        <div className={cn(
                                            "grid gap-4",
                                            isSquareGalleryTile ? "grid-cols-4 md:grid-cols-5" : "grid-cols-3"
                                        )}>
                                            {Array.from({ length: isSquareGalleryTile ? 10 : 6 }).map((_, i) => (
                                                <Skeleton
                                                    key={i}
                                                    className={cn(
                                                        isSquareGalleryTile ? "aspect-square" : "aspect-video",
                                                        isCircularGalleryTile ? "rounded-full" : "rounded-lg",
                                                    )}
                                                />
                                            ))}
                                        </div>
                                    ) : images.length === 0 ? (
                                        <div className="flex flex-col items-center justify-center h-64 text-center">
                                            <ImageIcon className="h-12 w-12 text-muted-foreground mb-4" />
                                            <p className="text-muted-foreground">No global images found</p>
                                            <p className="text-sm text-muted-foreground mt-2">
                                                Global images are managed by administrators
                                            </p>
                                        </div>
                                    ) : (
                                        <div className={cn(
                                            "grid gap-4",
                                            isSquareGalleryTile ? "grid-cols-4 md:grid-cols-5" : "grid-cols-3"
                                        )}>
                                            {images.map((image) => (
                                                <button
                                                    key={image.id}
                                                    onClick={() => {
                                                        onSelect(image);
                                                        onOpenChange(false);
                                                    }}
                                                    className={cn(
                                                        'group relative overflow-hidden border-2 transition-all',
                                                        isCircularGalleryTile
                                                            ? 'aspect-square rounded-full'
                                                            : isSquareGalleryTile
                                                              ? 'aspect-square rounded-lg'
                                                              : 'aspect-video rounded-lg',
                                                        currentImageId === image.id
                                                            ? 'border-primary'
                                                            : 'border-transparent hover:border-primary/50'
                                                    )}
                                                >
                                                    <Image
                                                        unoptimized
                                                        src={`/api/content-library/proxy-image?url=${encodeURIComponent(isCircularGalleryTile ? image.url : (image.thumbnailUrl || image.url))}`}
                                                        alt={image.title || 'Image'}
                                                        fill
                                                        className={cn(
                                                            "transition-transform group-hover:scale-105",
                                                            isCircularGalleryTile ? "object-cover" : "object-contain bg-muted/50"
                                                        )}
                                                        sizes={isSquareGalleryTile ? "(max-width: 768px) 25vw, 20vw" : "(max-width: 768px) 100vw, 33vw"}
                                                    />
                                                    {!isCircularGalleryTile && image.title && (
                                                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                                                            <p className="text-white text-sm truncate">{image.title}</p>
                                                        </div>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </ScrollArea>
                            </TabsContent>
                        </>
                    )}

                    {allowUpload && (
                        <TabsContent value="upload" className="mt-4">
                            <div className="flex flex-col h-[520px]">
                                <ScrollArea className="flex-1 pr-4">
                                    <div className="space-y-4">
                                        {!previewUrl ? (
                                            <div
                                                {...getRootProps()}
                                                className={cn(
                                                    'border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors',
                                                    isDragActive
                                                        ? 'border-primary bg-primary/5'
                                                        : 'border-muted-foreground/25 hover:border-primary/50'
                                                )}
                                            >
                                                <input {...getInputProps()} />
                                                <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                                                <p className="text-lg font-medium mb-2">
                                                    {isDragActive ? 'Drop image here' : 'Drag & drop an image'}
                                                </p>
                                                <p className="text-sm text-muted-foreground">
                                                    or click to browse (max 10MB)
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="space-y-4">
                                                <div className="relative">
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="absolute top-2 right-2 z-10"
                                                        onClick={() => {
                                                            setSelectedFile(null);
                                                            setPreviewUrl(null);
                                                            setCrop(undefined);
                                                            setCompletedCrop(undefined);
                                                        }}
                                                    >
                                                        <X className="h-4 w-4" />
                                                    </Button>

                                                    {useUploadCropper ? (
                                                        <div className="flex overflow-auto h-[300px] w-full relative bg-muted/50 rounded-md border">
                                                            <div className="m-auto p-4 flex items-center justify-center min-h-0">
                                                                <ReactCrop
                                                                    crop={crop}
                                                                    onChange={(c) => setCrop(c)}
                                                                    onComplete={(c) => setCompletedCrop(c)}
                                                                    aspect={cropAspect}
                                                                    circularCrop={cropCircular}
                                                                >
                                                                    <img
                                                                        ref={imgRef}
                                                                        src={previewUrl || ''}
                                                                        alt="Preview"
                                                                        style={getImageStyle()}
                                                                        onLoad={onImageLoad}
                                                                    />
                                                                </ReactCrop>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="flex overflow-auto h-[300px] w-full relative bg-muted/50 rounded-md border">
                                                            <div className="m-auto p-4 flex items-center justify-center min-h-0">
                                                                <img
                                                                    src={previewUrl || ''}
                                                                    alt="Preview"
                                                                    className="rounded-lg"
                                                                    style={getImageStyle()}
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="space-y-3">
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                        <div>
                                                            <Label htmlFor="category">Category</Label>
                                                            <Select
                                                                value={selectedCategory}
                                                                onValueChange={(v) => setSelectedCategory(v as ImageCategory)}
                                                            >
                                                                <SelectTrigger>
                                                                    <SelectValue />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    <SelectItem value="CONTENT_CARD">Content Card</SelectItem>
                                                                    <SelectItem value="ROOM_HEADER">Room Header</SelectItem>
                                                                    <SelectItem value="LOGO">Logo</SelectItem>
                                                                    <SelectItem value="AVATAR">Avatar</SelectItem>
                                                                    <SelectItem value="GENERAL">General</SelectItem>
                                                                </SelectContent>
                                                            </Select>
                                                        </div>
                                                        <div>
                                                            <Label htmlFor="title">Title (optional)</Label>
                                                            <Input
                                                                id="title"
                                                                value={title}
                                                                onChange={(e) => setTitle(e.target.value)}
                                                                placeholder="Short, human‑readable name"
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                        <div>
                                                            <Label htmlFor="description">Description (optional)</Label>
                                                            <Textarea
                                                                id="description"
                                                                value={description}
                                                                onChange={(e) => setDescription(e.target.value)}
                                                                placeholder="Where or how this image is used"
                                                                rows={3}
                                                            />
                                                        </div>
                                                        <div>
                                                            <Label htmlFor="tags">Tags (optional)</Label>
                                                            <Input
                                                                id="tags"
                                                                value={tags}
                                                                onChange={(e) => setTags(e.target.value)}
                                                                placeholder="e.g., welcome, hero, product (comma-separated)"
                                                            />
                                                            <p className="mt-1 text-xs text-muted-foreground">
                                                                Used for searching and filtering in the library.
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </ScrollArea>

                                {previewUrl && (
                                    <div className="mt-4 flex gap-2 border-t pt-4 justify-end bg-background">
                                        <Button
                                            variant="outline"
                                            onClick={() => {
                                                setSelectedFile(null);
                                                setPreviewUrl(null);
                                                setCrop(undefined);
                                                setCompletedCrop(undefined);
                                            }}
                                            className="min-w-[120px]"
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            onClick={handleUpload}
                                            disabled={isUploading}
                                            className="min-w-[160px]"
                                        >
                                            {isUploading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                            Upload
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </TabsContent>
                    )}
                </Tabs>
            </DialogContent>
        </Dialog>
    );
}
